import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { useSession } from '@descope/react-sdk';
import { clearUserProfile } from '@center-pwa/reducers/userProfileReducer';

/**
 * Sends the user to `/login` in the same render in which a signed-in Descope session ends.
 *
 * When the session goes, `ApiClientsProvider` publishes no clients and every mounted
 * `useApiClients()` caller (Profile, Preferences, Security, …) throws; with no error boundary above
 * them React unmounts the whole tree and the user is left on a blank page. Rendering `Navigate`
 * instead of the children on that pass means those consumers never render against a dead session,
 * whichever control ended it (the Descope widget's own Logout, Sign out everywhere, a revoked
 * session picked up on refresh). A visit that never had a session is untouched — the app's own
 * initialisation still routes it to `/login`.
 */
const SessionEndGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isSessionLoading } = useSession();
  const dispatch = useDispatch();
  const hadSession = useRef(false);

  if (isAuthenticated) {
    hadSession.current = true;
  }

  const sessionEnded = hadSession.current && !isSessionLoading && !isAuthenticated;

  useEffect(() => {
    if (sessionEnded) dispatch(clearUserProfile());
  }, [sessionEnded, dispatch]);

  if (sessionEnded) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

export default SessionEndGuard;
